'use client'

import React, { useRef } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ChevronDown, Maximize, Minimize } from 'lucide-react';
import { PeriodType } from '../types'; // Assuming types are in ../types

// Define Props for the controls component
interface ActivityGraphControlsProps {
  chartType: 'bar' | 'area' | 'percent' | 'line';
  breakdownType: 'sentiment' | 'category' | 'brand' | 'source';
  selectedPeriod: PeriodType;
  isFullscreen: boolean;
  onChartTypeChange: (value: 'bar' | 'area' | 'percent' | 'line') => void;
  onBreakdownTypeChange: (value: 'sentiment' | 'category' | 'brand' | 'source') => void;
  onPeriodChange: (value: PeriodType, e?: React.ChangeEvent<HTMLSelectElement>) => void; // Match original signature
  onToggleFullscreen: () => void;
  dashboardMode?: boolean;
}

export function ActivityGraphControls({
  chartType,
  breakdownType,
  selectedPeriod,
  isFullscreen,
  onChartTypeChange,
  onBreakdownTypeChange,
  onPeriodChange,
  onToggleFullscreen,
  dashboardMode = false,
}: ActivityGraphControlsProps) {
  const isMobile = useRef(typeof window !== 'undefined' ? window.innerWidth < 640 : false);

  // For dashboard mode, we don't need the title as it's already in the card header
  if (dashboardMode) {
    return (
      <div className="flex flex-row justify-center items-center mb-0 px-1 gap-1 mt-[-8px]">
        {/* Controls centered */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-600 opacity-80">Chart:</span>
          <div className="relative min-w-[65px]">
            <select 
              value={chartType} 
              onChange={(e) => onChartTypeChange(e.target.value as typeof chartType)}
              className="w-full appearance-none bg-white border border-gray-300 text-gray-700 py-0 px-1 pr-6 rounded-md text-xs leading-tight focus:outline-none focus:ring-1"
            >
              <option value="bar">Bar</option>
              <option value="area">Area</option>
              <option value="percent">Percent</option>
              <option value="line">Line</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1.5 text-gray-700">
              <ChevronDown className="h-2 w-2" />
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-600 opacity-80">Breakdown:</span>
          <div className="relative min-w-[75px]"> 
            <select 
              value={breakdownType} 
              onChange={(e) => onBreakdownTypeChange(e.target.value as typeof breakdownType)}
              className="w-full appearance-none bg-white border border-gray-300 text-gray-700 py-0 px-1 pr-6 rounded-md text-xs leading-tight focus:outline-none focus:ring-1"
            >
              <option value="sentiment">Sentiment</option>
              <option value="category">Category</option>
              <option value="brand">Brand</option>
              <option value="source">Source</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1.5 text-gray-700">
              <ChevronDown className="h-2 w-2" />
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-600 opacity-80">Period:</span>
          <div className="relative min-w-[95px]"> 
            <select 
              value={selectedPeriod}
              onChange={(e) => onPeriodChange(e.target.value as PeriodType, e)}
              onClick={(e) => e.stopPropagation()} 
              className="period-selector w-full appearance-none bg-white border border-gray-300 text-gray-700 py-0 px-1 pr-6 rounded-md text-xs leading-tight focus:outline-none focus:ring-1"
            >
              <option value="Monthly">Monthly</option>
              <option value="Weekly">Weekly</option>
              <option value="Daily">Daily</option>
              <option value="Quarterly">Quarterly</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1.5 text-gray-700">
              <ChevronDown className="h-2 w-2" />
            </div>
          </div>
        </div>
      
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onToggleFullscreen}
          onTouchEnd={(e) => {
            e.preventDefault(); 
            onToggleFullscreen();
          }}
          className="text-gray-600 hover:text-[#8A6FE8] p-0.5 h-6 w-6"
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {isFullscreen ? <Minimize size={14} /> : <Maximize size={14} />}
        </Button>
      </div>
    );
  }

  // Original layout for non-dashboard mode
  return (
    <div className={cn(
      "flex flex-wrap sm:flex-row justify-center items-center mb-1 sm:mb-2 px-1 sm:px-0 gap-2 sm:gap-4",
      isFullscreen ? "sticky top-0 bg-white z-10 py-2 -mx-4 px-4 border-b" : "" 
    )}>
      <h3 className="text-base sm:text-lg font-semibold text-[#2D3748]">Media Clips Activity</h3>
      
      <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3"> 
        {/* Chart Type Selector */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-600 whitespace-nowrap">Chart:</span>
          <div className="relative min-w-[80px]">
            <select 
              value={chartType} 
              onChange={(e) => onChartTypeChange(e.target.value as typeof chartType)}
              className="w-full appearance-none bg-white border border-gray-300 text-gray-700 py-0.5 px-1 pr-7 rounded-lg text-xs leading-tight focus:outline-none focus:ring-2 focus:ring-[#8A6FE8] focus:border-transparent"
            >
              <option value="bar">Bar</option>
              <option value="area">Area</option>
              <option value="percent">Percent</option>
              <option value="line">Line</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <ChevronDown className="h-3 w-3" />
            </div>
          </div>
        </div>
        
        {/* Breakdown Type Dropdown */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-600 whitespace-nowrap">Breakdown:</span>
          <div className="relative min-w-[90px]"> 
            <select 
              value={breakdownType} 
              onChange={(e) => onBreakdownTypeChange(e.target.value as typeof breakdownType)}
              className="w-full appearance-none bg-white border border-gray-300 text-gray-700 py-0.5 px-1 pr-7 rounded-lg text-xs leading-tight focus:outline-none focus:ring-2 focus:ring-[#8A6FE8] focus:border-transparent"
            >
              <option value="sentiment">Sentiment</option>
              <option value="category">Category</option>
              <option value="brand">Brand</option>
              <option value="source">Source</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <ChevronDown className="h-3 w-3" />
            </div>
          </div>
        </div>
        
        {/* Period Dropdown */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-600 whitespace-nowrap">Period:</span>
          <div className="relative min-w-[110px]"> 
            <select 
              value={selectedPeriod}
              onChange={(e) => onPeriodChange(e.target.value as PeriodType, e)}
              onClick={(e) => e.stopPropagation()} // Keep safeguard
              className="period-selector w-full appearance-none bg-white border border-gray-300 text-gray-700 py-0.5 px-1 pr-7 rounded-lg text-xs leading-tight focus:outline-none focus:ring-2 focus:ring-[#8A6FE8] focus:border-transparent"
            >
              <option value="Monthly">Monthly</option>
              <option value="Weekly">Weekly</option>
              <option value="Daily">Daily</option>
              <option value="Quarterly">Quarterly</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <ChevronDown className="h-3 w-3" />
            </div>
          </div>
        </div>
      
        {/* Fullscreen Toggle Button */}
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onToggleFullscreen}
          onTouchEnd={(e) => { // Keep mobile fix
            e.preventDefault(); 
            onToggleFullscreen();
          }}
          className="text-gray-600 hover:text-[#8A6FE8] p-1"
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
        </Button>
      </div>
    </div>
  );
} 